/**
 * The upload: grant, PUT and completion (FR-007; P.3, P.4, U.1, U.2, U.3,
 * SEC.8; research §1.15; contracts/ui-api.md "New run"; post-plan S9, C12;
 * post-tasks E5).
 *
 * `start` hands the file and its resolved content type to
 * `lib/files.uploadFile`, the one function that runs the grant, the PUT and the
 * completion, inside a ref-guarded promise: a second drop while one is in
 * flight is ignored. There is no `useMutation` and no TanStack retry: the grant
 * body never leaves `uploadFile`, so the presigned URL reaches no state and no
 * cache (S9). The hook keeps the completion body, a phase, and (in a ref) the
 * file for Retry.
 *
 * Answers are mapped by problem type and status, never by `detail` (C12). The
 * hook's own renderings: 415 and 413 are the dropzone's refusal; a filename
 * `validation-failed` is inline; `layout-version-unknown` names what is
 * accepted; `too-many-records` is the P.4 band with Remove; `try-again` is the
 * retry state with its `detail`; a 410 U.1 is an expired grant; a failed PUT
 * is register 16 `storageFailed`. Everything else renders through ProblemBand.
 *
 * Retry re-runs `uploadFile` from the grant (a new upload; nothing was
 * charged): `uploadFile` returns the completion body only, so the upload id is
 * not known on a `try-again`. Retry runs only on the reporter's action.
 */
import { useCallback, useRef, useState } from 'react'

import { ApiError, type Problem } from '@/lib/api'
import type { RunPath } from '@/lib/api/runs'
import { TransferError, uploadFile } from '@/lib/files'
import { PROBLEM } from '@/lib/problems'

export interface ValidatedUpload {
  upload_id: string
  record_count: number
  filename: string
  size_bytes: number
}

export type UploadFailure =
  | { reason: 'storage' }
  | { reason: 'layout-version'; accepted: string[] }
  | { reason: 'problem'; problem: Problem }
  | { reason: 'generic' }

export type UploadState =
  | { kind: 'idle'; refused?: true; fieldError?: string }
  | { kind: 'validating'; filename: string; sizeBytes: number }
  | { kind: 'validated'; upload: ValidatedUpload }
  | { kind: 'overCap'; problem: Problem; filename: string; sizeBytes: number }
  | { kind: 'retry'; detail: string; filename: string; sizeBytes: number }
  | { kind: 'expired' }
  | { kind: 'failed'; failure: UploadFailure }

export type UploadOutcome = { kind: 'validated' } | { kind: 'rejected'; runId: string } | { kind: 'settled' } | { kind: 'ignored' }

export interface UseUpload {
  state: UploadState
  start(file: File, contentType: string): Promise<UploadOutcome>
  retry(): Promise<UploadOutcome>
  remove(): void
}

const IDLE: UploadState = { kind: 'idle' }

/** The state a refused grant or completion leaves, by type and status (never `detail`). */
function stateFor(err: unknown, file: File): UploadState {
  if (err instanceof TransferError) return { kind: 'failed', failure: { reason: 'storage' } }
  if (!(err instanceof ApiError)) return { kind: 'failed', failure: { reason: 'generic' } }
  const p = err.problem
  const at = { filename: file.name, sizeBytes: file.size }
  switch (p.type) {
    case PROBLEM.UPLOAD_UNSUPPORTED_TYPE:
    case PROBLEM.UPLOAD_TOO_LARGE:
      return { kind: 'idle', refused: true }
    case PROBLEM.VALIDATION_FAILED: {
      const onFilename = p.errors?.find((e) => e.field === 'filename')
      return onFilename
        ? { kind: 'idle', fieldError: onFilename.message }
        : { kind: 'failed', failure: { reason: 'generic' } }
    }
    case PROBLEM.LAYOUT_VERSION_UNKNOWN:
      return { kind: 'failed', failure: { reason: 'layout-version', accepted: p.accepted ?? [] } }
    case PROBLEM.TOO_MANY_RECORDS:
      return { kind: 'overCap', problem: p, ...at }
    case PROBLEM.TRY_AGAIN:
      return p.detail ? { kind: 'retry', detail: p.detail, ...at } : { kind: 'failed', failure: { reason: 'problem', problem: p } }
    case PROBLEM.NOT_FOUND:
      if (p.status === 410 && p.requirement === 'U.1') return { kind: 'expired' }
      return { kind: 'failed', failure: { reason: 'problem', problem: p } }
    default:
      return { kind: 'failed', failure: { reason: 'problem', problem: p } }
  }
}

export function useUpload(path: RunPath): UseUpload {
  const [state, setState] = useState<UploadState>(IDLE)
  const inFlight = useRef<Promise<UploadOutcome> | null>(null)
  const last = useRef<{ file: File; contentType: string } | null>(null)
  /** Bumped by remove(): a late answer for a removed upload is dropped. */
  const generation = useRef(0)

  const run = useCallback(
    (file: File, contentType: string): Promise<UploadOutcome> => {
      if (inFlight.current) return Promise.resolve({ kind: 'ignored' })
      last.current = { file, contentType }
      const mine = ++generation.current
      setState({ kind: 'validating', filename: file.name, sizeBytes: file.size })
      const work = (async (): Promise<UploadOutcome> => {
        try {
          const upload = await uploadFile(file, path, contentType)
          if (mine !== generation.current) return { kind: 'ignored' }
          // A validated body always carries record_count; without it the estimate would rest on a silent 0.
          if (upload.state === 'validated' && upload.record_count !== undefined) {
            setState({
              kind: 'validated',
              upload: {
                upload_id: upload.upload_id,
                record_count: upload.record_count,
                filename: upload.filename ?? file.name,
                size_bytes: upload.size_bytes ?? file.size,
              },
            })
            return { kind: 'validated' }
          }
          if (upload.state === 'rejected' && upload.run_id !== undefined) {
            setState(IDLE)
            return { kind: 'rejected', runId: upload.run_id }
          }
          setState({ kind: 'failed', failure: { reason: 'generic' } })
          return { kind: 'settled' }
        } catch (err) {
          if (mine !== generation.current) return { kind: 'ignored' }
          setState(stateFor(err, file))
          return { kind: 'settled' }
        } finally {
          // Only this upload's own guard: a removed upload's late answer leaves a newer one alone.
          if (mine === generation.current) inFlight.current = null
        }
      })()
      inFlight.current = work
      return work
    },
    [path],
  )

  const retry = useCallback((): Promise<UploadOutcome> => {
    const again = last.current
    if (!again) return Promise.resolve({ kind: 'ignored' })
    return run(again.file, again.contentType)
  }, [run])

  const remove = useCallback(() => {
    generation.current++
    inFlight.current = null
    last.current = null
    setState(IDLE)
  }, [])

  return { state, start: run, retry, remove }
}
